import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Pencil, CheckCircle2, X, CalendarClock } from "lucide-react";

const STATUS_COLOR = {
  Overdue: "bg-red-100 text-red-800",
  "Due Soon": "bg-amber-100 text-amber-800",
  Scheduled: "bg-sky-100 text-sky-800",
  Completed: "bg-emerald-100 text-emerald-800",
};

const FREQ_OPTIONS = [
  { type: "daily", days: 1, label: "Daily" },
  { type: "weekly", days: 7, label: "Weekly" },
  { type: "fortnightly", days: 14, label: "Fortnightly" },
  { type: "monthly", days: 30, label: "Monthly" },
  { type: "quarterly", days: 90, label: "Quarterly" },
  { type: "six_monthly", days: 182, label: "Six-monthly" },
  { type: "annual", days: 365, label: "Annual" },
  { type: "ad_hoc", days: null, label: "Ad-hoc (no schedule)" },
];

function priorityRank(p) {
  return { Critical: 0, High: 1, Medium: 2, Low: 3 }[p] ?? 4;
}
function statusRank(s) {
  return { "Overdue": 0, "Due Soon": 1, "Scheduled": 2, "Completed": 3 }[s] ?? 4;
}
function stripLocation(title, locSet) {
  const idx = title.indexOf(" — ");
  if (idx === -1) return { base: title, loc: null };
  const first = title.slice(0, idx);
  if (locSet.has(first)) return { base: title.slice(idx + 3), loc: first };
  return { base: title, loc: null };
}

export default function Maintenance() {
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [regulatory, setRegulatory] = useState("all");
  const [location, setLocation] = useState("all");
  const [view, setView] = useState("grouped");
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    const p = {};
    if (filter !== "all") p.filter = filter;
    if (regulatory === "yes") p.regulatory = true;
    if (regulatory === "no") p.regulatory = false;
    if (location !== "all") p.location = location;
    const { data } = await api.get("/maintenance", { params: p });
    setRows(data);
    setSelected(new Set());
  }, [filter, regulatory, location]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/maintenance/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
  }, []);

  const locSet = useMemo(() => new Set(assets.map((a) => a.location).filter(Boolean)), [assets]);

  const enriched = useMemo(
    () => rows.map((t) => ({ ...t, ...stripLocation(t.title || "", locSet) })),
    [rows, locSet],
  );

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of enriched) {
      const key = t.base;
      if (!map.has(key)) map.set(key, { base: key, items: [], asset_group: t.asset_group, is_regulatory: t.is_regulatory, frequency_text: t.frequency_text });
      const g = map.get(key);
      g.items.push(t);
      if (t.is_regulatory) g.is_regulatory = true;
    }
    for (const g of map.values()) {
      g.count = g.items.length;
      g.max_priority = g.items.reduce((acc, x) => priorityRank(x.priority) < priorityRank(acc) ? x.priority : acc, "Low");
      g.worst_status = g.items.reduce((acc, x) => statusRank(x.status) < statusRank(acc) ? x.status : acc, "Scheduled");
      const dues = g.items.map((x) => x.next_due_date).filter(Boolean).sort();
      g.earliest_due = dues[0] || null;
    }
    return Array.from(map.values()).sort((a, b) => (a.earliest_due || "9999").localeCompare(b.earliest_due || "9999"));
  }, [enriched]);

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleGroup = (g) => {
    const next = new Set(selected);
    const allIn = g.items.every((t) => next.has(t.id));
    for (const t of g.items) { if (allIn) next.delete(t.id); else next.add(t.id); }
    setSelected(next);
  };
  const toggleExpand = (base) => {
    const next = new Set(expanded);
    if (next.has(base)) next.delete(base); else next.add(base);
    setExpanded(next);
  };

  const complete = async (id) => {
    try { await api.post(`/maintenance/${id}/complete`); toast.success("Marked complete"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkComplete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} task(s) complete? Each will recalculate its next due date.`)) return;
    try {
      const { data } = await api.post("/maintenance/bulk-complete", { ids: Array.from(selected) });
      toast.success(`Completed ${data.completed}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const bulkReschedule = async (days) => {
    if (selected.size === 0) return;
    const label = days > 0 ? `push ${days} day(s) later` : `pull ${Math.abs(days)} day(s) earlier`;
    if (!window.confirm(`Reschedule ${selected.size} task(s) — ${label}?`)) return;
    try {
      const { data } = await api.post("/maintenance/bulk-reschedule",
                                      { ids: Array.from(selected), days });
      toast.success(`Rescheduled ${data.rescheduled} task(s) by ${days > 0 ? "+" : ""}${days} day(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({
      frequency_text: t.frequency_text || "",
      frequency_type: t.frequency_type || "ad_hoc",
      frequency_days: t.frequency_days ?? null,
      next_due_date: (t.next_due_date || "").slice(0, 10),
      priority: t.priority || "Medium",
      is_regulatory: !!t.is_regulatory,
    });
  };
  const applyFreqType = (type) => {
    const preset = FREQ_OPTIONS.find((o) => o.type === type);
    setEditForm((f) => ({ ...f, frequency_type: type, frequency_days: preset?.days ?? null,
      frequency_text: f.frequency_text || preset?.label || "" }));
  };
  const saveEdit = async () => {
    if (!editing) return;
    const payload = { ...editForm };
    if (payload.next_due_date) payload.next_due_date = new Date(payload.next_due_date + "T09:00:00Z").toISOString();
    else delete payload.next_due_date;
    try { await api.put(`/maintenance/${editing.id}`, payload); toast.success("Task updated"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const FILTERS = [
    ["all","All open"], ["overdue","Overdue"], ["due_7","Due 7 days"], ["due_30","Due 30 days"],
    ["due_90","Due 90 days"], ["high_priority","High priority"],
  ];

  return (
    <div className="space-y-4" data-testid="maintenance-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Schedule</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Maintenance</h1>
          <p className="text-sm text-slate-600 mt-1">{rows.length} task(s){location!=="all"?` in ${location}`:""}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k,label]) => (
          <button
            key={k}
            data-testid={`maint-filter-${k}`}
            onClick={() => setParams(k==="all"?{}:{ filter:k })}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors duration-200 ${filter===k?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
          >{label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-40" data-testid="maint-view"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="grouped">Grouped by task</SelectItem>
              <SelectItem value="flat">Flat list</SelectItem>
            </SelectContent>
          </Select>
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="w-52" data-testid="maint-filter-location"><SelectValue placeholder="Location"/></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All locations</SelectItem>
              {locations.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={regulatory} onValueChange={setRegulatory}>
            <SelectTrigger className="w-40" data-testid="maint-filter-regulatory"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="yes">Regulatory only</SelectItem>
              <SelectItem value="no">General only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-0 z-20 bg-slate-900 text-slate-50 rounded-md flex items-center justify-between px-4 py-2 shadow-md flex-wrap gap-2" data-testid="bulk-action-bar">
          <div className="text-sm"><b>{selected.size}</b> selected</div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="flex items-center gap-1 text-xs text-slate-300 mr-1"><CalendarClock className="w-3.5 h-3.5"/>Reschedule:</div>
            <Button size="sm" variant="secondary" onClick={()=>bulkReschedule(-7)} data-testid="bulk-reschedule-minus-week">−1 week</Button>
            <Button size="sm" variant="secondary" onClick={()=>bulkReschedule(7)} data-testid="bulk-reschedule-plus-week">+1 week</Button>
            <Button size="sm" variant="secondary" onClick={()=>bulkReschedule(14)} data-testid="bulk-reschedule-plus-2week">+2 weeks</Button>
            <Button size="sm" variant="secondary" onClick={()=>bulkReschedule(30)} data-testid="bulk-reschedule-plus-month">+1 month</Button>
            <span className="mx-1 text-slate-600">·</span>
            <Button size="sm" variant="secondary" onClick={()=>setSelected(new Set())}><X className="w-4 h-4 mr-1"/>Clear</Button>
            <Button size="sm" onClick={bulkComplete} data-testid="bulk-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1"/>Complete selected</Button>
          </div>
        </div>
      )}

      <Card className="border-slate-200"><CardContent className="p-4">
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table className="dense-table">
            <TableHeader><TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Task</TableHead>
              <TableHead>Asset group</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Next due</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {view === "flat" && enriched.map((t)=>(
                <TableRow key={t.id} data-testid={`maint-row-${t.id}`}>
                  <TableCell><Checkbox checked={selected.has(t.id)} onCheckedChange={()=>toggleOne(t.id)} data-testid={`maint-check-${t.id}`}/></TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{t.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{t.description}</div>
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs">{t.asset_group||"—"}</TableCell>
                  <TableCell className="text-xs">{t.frequency_text||"—"}</TableCell>
                  <TableCell className="font-mono text-xs">{t.next_due_date ? new Date(t.next_due_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell><Badge variant="outline">{t.priority}</Badge></TableCell>
                  <TableCell><Badge className={`${STATUS_COLOR[t.status]||"bg-slate-100 text-slate-700"} border-0`}>{t.status}</Badge></TableCell>
                  <TableCell>{t.is_regulatory ? <Badge className="bg-red-50 text-red-700 border-red-200">Regulatory</Badge> : <Badge variant="outline">General</Badge>}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(t)} data-testid={`maint-edit-${t.id}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    <Button size="sm" variant="outline" onClick={()=>complete(t.id)} data-testid={`maint-complete-${t.id}`}>Complete</Button>
                  </TableCell>
                </TableRow>
              ))}

              {view === "grouped" && groups.map((g) => {
                const isExpanded = expanded.has(g.base);
                const allSelected = g.items.length > 0 && g.items.every((t)=>selected.has(t.id));
                const singleton = g.count === 1;
                return (
                  <Fragment key={`grp-${g.base}`}>
                    <TableRow className="bg-slate-50 hover:bg-slate-100" data-testid={`maint-group-${g.base}`}>
                      <TableCell><Checkbox checked={allSelected} onCheckedChange={()=>toggleGroup(g)}/></TableCell>
                      <TableCell onClick={()=>!singleton && toggleExpand(g.base)} className={!singleton ? "cursor-pointer" : ""}>
                        <div className="flex items-center gap-2">
                          {!singleton && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>)}
                          <div>
                            <div className="font-medium text-slate-900">{g.base}</div>
                            <div className="text-xs text-slate-500">{singleton ? (g.items[0].loc || "no location") : `${g.count} instance(s)`}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-xs">{g.asset_group || "—"}</TableCell>
                      <TableCell className="text-xs">{g.frequency_text||"—"}</TableCell>
                      <TableCell className="font-mono text-xs">{g.earliest_due ? new Date(g.earliest_due).toLocaleDateString() : "—"}</TableCell>
                      <TableCell><Badge variant="outline">{g.max_priority}</Badge></TableCell>
                      <TableCell><Badge className={`${STATUS_COLOR[g.worst_status]||"bg-slate-100 text-slate-700"} border-0`}>{g.worst_status}</Badge></TableCell>
                      <TableCell>{g.is_regulatory ? <Badge className="bg-red-50 text-red-700 border-red-200">Regulatory</Badge> : <Badge variant="outline">General</Badge>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {singleton && <Button size="sm" variant="ghost" onClick={()=>openEdit(g.items[0])}><Pencil className="w-3.5 h-3.5"/></Button>}
                        {singleton
                          ? <Button size="sm" variant="outline" onClick={()=>complete(g.items[0].id)}>Complete</Button>
                          : <Button size="sm" variant="outline" onClick={()=>toggleGroup(g)}>Select all</Button>}
                      </TableCell>
                    </TableRow>
                    {isExpanded && !singleton && g.items.map((t)=>(
                      <TableRow key={t.id} className="bg-white" data-testid={`maint-row-${t.id}`}>
                        <TableCell className="pl-10"><Checkbox checked={selected.has(t.id)} onCheckedChange={()=>toggleOne(t.id)}/></TableCell>
                        <TableCell className="pl-10">
                          <div className="text-sm text-slate-800">{t.loc || "—"}</div>
                          <div className="text-xs text-slate-500 line-clamp-1">{t.description}</div>
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs">{t.asset_group||"—"}</TableCell>
                        <TableCell className="text-xs">{t.frequency_text||"—"}</TableCell>
                        <TableCell className="font-mono text-xs">{t.next_due_date ? new Date(t.next_due_date).toLocaleDateString() : "—"}</TableCell>
                        <TableCell><Badge variant="outline">{t.priority}</Badge></TableCell>
                        <TableCell><Badge className={`${STATUS_COLOR[t.status]||"bg-slate-100 text-slate-700"} border-0`}>{t.status}</Badge></TableCell>
                        <TableCell>{t.is_regulatory ? <Badge className="bg-red-50 text-red-700 border-red-200">Regulatory</Badge> : <Badge variant="outline">General</Badge>}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={()=>openEdit(t)}><Pencil className="w-3.5 h-3.5"/></Button>
                          <Button size="sm" variant="outline" onClick={()=>complete(t.id)}>Complete</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
              {rows.length===0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No tasks match.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      <Dialog open={!!editing} onOpenChange={(o)=>!o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit recurrence</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm font-medium text-slate-900 line-clamp-2">{editing?.title}</div>
            <div>
              <Label>Frequency</Label>
              <Select value={editForm.frequency_type || "ad_hoc"} onValueChange={applyFreqType}>
                <SelectTrigger data-testid="edit-freq-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {FREQ_OPTIONS.map((o)=>(<SelectItem key={o.type} value={o.type}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Human-readable frequency</Label>
              <Input value={editForm.frequency_text || ""} onChange={(e)=>setEditForm({...editForm, frequency_text:e.target.value})} data-testid="edit-freq-text"/>
              <div className="text-xs text-slate-500 mt-1">Displayed on lists and calendars.</div>
            </div>
            <div>
              <Label>Next due date</Label>
              <Input type="date" value={editForm.next_due_date || ""} onChange={(e)=>setEditForm({...editForm, next_due_date:e.target.value})} data-testid="edit-next-due"/>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={editForm.priority || "Medium"} onValueChange={(v)=>setEditForm({...editForm, priority:v})}>
                <SelectTrigger data-testid="edit-priority"><SelectValue/></SelectTrigger>
                <SelectContent>{["Critical","High","Medium","Low"].map((p)=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={!!editForm.is_regulatory} onCheckedChange={(v)=>setEditForm({...editForm, is_regulatory:!!v})}/>
              Regulatory / compliance driven
            </label>
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
